import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo from "@/components/Logo";
import { NAV } from "@/lib/content";
import { useLang } from "@/lib/i18n";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { lang, setLang, t } = useLang();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? "bg-obsidian/80 backdrop-blur-xl border-b border-primary-gold/10 py-4" : "bg-transparent py-6"}`}
      >
        <div className="container-x flex items-center justify-between">
          <Link to="/" data-testid="header-logo-link" aria-label="Intersect Holding home">
            <Logo width={scrolled ? 150 : 168} className="transition-all duration-500" />
          </Link>

          <nav className="hidden lg:flex items-center gap-9" data-testid="desktop-nav">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to.replace("/", "")}`}
                className={({ isActive }) =>
                  `relative text-[13px] tracking-wide uppercase transition-colors duration-300 after:content-[''] after:absolute after:left-0 after:-bottom-1.5 after:h-px after:bg-primary-gold after:transition-all after:duration-300 ${isActive ? "text-primary-gold after:w-full" : "text-soft-stone hover:text-primary-gold after:w-0 hover:after:w-full"}`
                }
              >
                {t(n.label)}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-6">
            <div className="flex items-center gap-2 text-xs tracking-wide text-muted-text" data-testid="lang-selector">
              <button onClick={() => setLang("en")} data-testid="lang-toggle-en" className={lang === "en" ? "text-primary-gold" : "hover:text-soft-stone"}>EN</button>
              <span className="text-white/20">|</span>
              <button onClick={() => setLang("ar")} data-testid="lang-toggle-ar" className={`font-arabic ${lang === "ar" ? "text-primary-gold" : "hover:text-soft-stone"}`}>العربية</button>
            </div>
            <Link to="/contact" data-testid="header-cta" className="btn-gold">{t("Start a Conversation")}</Link>
          </div>

          <div className="flex items-center gap-3 lg:hidden">
            <div className="flex items-center gap-1.5 text-xs tracking-wide text-muted-text" data-testid="lang-selector-mobile">
              <button onClick={() => setLang("en")} data-testid="lang-toggle-en-mobile" className={lang === "en" ? "text-primary-gold" : ""}>EN</button>
              <span className="text-white/20">|</span>
              <button onClick={() => setLang("ar")} data-testid="lang-toggle-ar-mobile" className={`font-arabic text-sm ${lang === "ar" ? "text-primary-gold" : ""}`}>ع</button>
            </div>
            <button
              data-testid="mobile-menu-toggle"
              className="text-ivory p-2"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
            >
              <Menu strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[60] bg-obsidian flex flex-col"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
          >
            <div className="container-x flex items-center justify-between py-6">
              <Logo width={150} />
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="text-ivory p-2" aria-label="Close menu">
                <X strokeWidth={1.5} />
              </button>
            </div>
            <div className="container-x flex-1 flex flex-col justify-center gap-2">
              {NAV.map((n, i) => (
                <motion.div key={n.to}
                  initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i + 0.1 }}
                  className="flex items-baseline gap-4 border-b border-white/5 py-4">
                  <span className="text-primary-gold text-xs tracking-widest">{String(i + 1).padStart(2, "0")}</span>
                  <Link to={n.to} className="text-3xl font-light tracking-tight text-ivory hover:text-primary-gold transition-colors">{t(n.label)}</Link>
                </motion.div>
              ))}
              <Link to="/contact" className="btn-gold mt-8 w-fit" data-testid="mobile-cta">{t("Start a Conversation")}</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
